import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { FormField } from '~/components/system/form-field';
import { DateInput } from '~/components/date-input';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useSeasonMutations } from '~/hooks/use-seasons';
import { seasonSchema, EMPTY_SEASON, type SeasonFormValues } from '~/lib/schemas/season';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: SeasonFormValues | null;
}

/** Add / edit a season (Σεζόν). */
export function SeasonEditDialog({ open, onOpenChange, initial }: Props) {
	const { t } = useTranslation();
	const { create, update } = useSeasonMutations();
	const form = useForm<SeasonFormValues>({
		resolver: zodResolver(seasonSchema) as Resolver<SeasonFormValues>,
		defaultValues: initial ?? EMPTY_SEASON,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, control, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_SEASON);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('catalog.seasons.editTitle') : t('catalog.seasons.addTitle')}</DialogTitle>
					<DialogDescription>{t('catalog.seasons.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('catalog.seasons.columns.name')} error={formState.errors.nm?.message}>
						<Input {...register('nm')} maxLength={100} aria-invalid={Boolean(formState.errors.nm)} autoFocus />
					</FormField>
					<FormField label={t('catalog.seasons.columns.code')} error={formState.errors.cd?.message}>
						<Input {...register('cd')} maxLength={50} aria-invalid={Boolean(formState.errors.cd)} />
					</FormField>
					<div className="grid grid-cols-2 gap-3">
						<FormField label={t('catalog.seasons.columns.startDate')} error={formState.errors.sdt?.message}>
							<Controller
								control={control}
								name="sdt"
								render={({ field }) => (
									<DateInput
										value={field.value ? new Date(field.value) : null}
										onChange={d => field.onChange(d ? d.toISOString() : null)}
									/>
								)}
							/>
						</FormField>
						<FormField label={t('catalog.seasons.columns.endDate')} error={formState.errors.edt?.message}>
							<Controller
								control={control}
								name="edt"
								render={({ field }) => (
									<DateInput
										value={field.value ? new Date(field.value) : null}
										onChange={d => field.onChange(d ? d.toISOString() : null)}
									/>
								)}
							/>
						</FormField>
					</div>
					<div className="grid grid-cols-2 gap-3">
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="se-iv">{t('catalog.seasons.columns.visible')}</Label>
							<Switch id="se-iv" checked={watch('iv')} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
						</div>
						<FormField label={t('catalog.seasons.columns.sort')} error={formState.errors.si?.message}>
							<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
						</FormField>
					</div>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
